import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  EyeIcon,
  EyeSlashIcon,
  CalendarIcon,
  KeyIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EnvelopeIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Switch } from "@headlessui/react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Table from "../../applicationUi/components/Table";
import SearchDropdown from "../../applicationUi/components/SearchDropdown";
import { logInfo, logError } from "../../utils/logger";
import {
  fetchClientPortalLogs,
  createPortalLog,
} from "../../store/thunks/clientPortalLogsThunks";
import {
  toggleClientPortalAccess,
  sendLoginDetails,
  updateBookingSettings,
} from "../../store/thunks/clientsThunk";
import { fetchClientById } from "../../store/thunks/clientsThunk";

export default function ClientPortal({ client: initialClient }) {
  const dispatch = useDispatch();
  const [client, setClient] = useState(initialClient);
  const clientPortalLogsState = useSelector(
    (state) => state.clientPortalLogs || {}
  );
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [emailAccess, setEmailAccess] = useState([]);
  const [bookingSettings, setBookingSettings] = useState({
    canBookOnline:
      client?.portalAccess?.bookingSettings?.canBookOnline || false,
    allowedAppointmentTypes:
      client?.portalAccess?.bookingSettings?.allowedAppointmentTypes || [],
    allowedClinicians:
      client?.portalAccess?.bookingSettings?.allowedClinicians || [],
    allowedClinics: client?.portalAccess?.bookingSettings?.allowedClinics || [],
  });
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [parentAccess, setParentAccess] = useState(client?.parentAccess || []);
  const [selectedServices, setSelectedServices] = useState([]);
  const [isBookingAccordionOpen, setIsBookingAccordionOpen] = useState(false);

  const [selectedClinicians, setSelectedClinicians] = useState([]);
  const [selectedClinics, setSelectedClinics] = useState([]);

  useEffect(() => {
    if (client?._id) {
      setIsLoading(true);
      setError(null);

      dispatch(fetchClientPortalLogs(client._id))
        .unwrap()
        .then((fetchedLogs) => {
          setLogs(fetchedLogs || []);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching logs:", err);
          setError(err.message || "Failed to fetch logs");
          setIsLoading(false);
        });

      fetchAppointmentTypes();
      fetchClinicians();
      initializeEmailAccess();
    }
  }, [client?._id, dispatch]);

  const initializeEmailAccess = () => {
    const emails = [];
    if (client?.email) {
      emails.push({
        firstName: client.firstName,
        lastName: client.lastName,
        email: client.email,
        userId: client._id,
        userType: "client",
        hasAccess:
          client?.portalAccess?.enabled &&
          client?.portalAccess?.email === client.email,
      });
    }
    client?.additionalEmails?.forEach((emailObj) => {
      emails.push({
        firstName: client.firstName,
        lastName: client.lastName,
        email: emailObj.email,
        userId: client._id,
        userType: "client",
        hasAccess:
          client?.portalAccess?.enabled &&
          client?.portalAccess?.email === emailObj.email,
      });
    });
    client?.parents?.forEach((parent) => {
      if (parent.email) {
        emails.push({
          firstName: parent.firstName,
          lastName: parent.lastName,
          email: parent.email,
          userId: parent._id,
          userType: "parent",
          hasAccess:
            parent?.portalAccess?.enabled &&
            parent?.portalAccess?.email === parent.email,
        });
      }
      parent?.additionalEmails?.forEach((emailObj) => {
        emails.push({
          firstName: parent.firstName,
          lastName: parent.lastName,
          email: emailObj.email,
          userId: parent._id,
          userType: "parent",
          hasAccess:
            parent?.portalAccess?.enabled &&
            parent?.portalAccess?.email === emailObj.email,
        });
      });
    });
    setEmailAccess(emails);
  };

  const handlePortalAccessToggle = async (email) => {
    try {
      const result = await dispatch(
        toggleClientPortalAccess({
          clientId: client._id,
          portalAccess: {
            ...client.portalAccess,
            enabled: !client.portalAccess?.enabled,
          },
          email: email,
        })
      ).unwrap();

      // Update the client object with the new portal access state
      const updatedClient = {
        ...client,
        portalAccess: result.portalAccess,
      };

      // Update the local state to reflect the changes
      setClient(updatedClient);

      // Update the emailAccess state to reflect the new access state
      setEmailAccess((prevEmailAccess) =>
        prevEmailAccess.map((access) =>
          access.email === email
            ? { ...access, hasAccess: result.portalAccess.enabled }
            : access
        )
      );

      // Show success message
      toast.success(
        `Portal access ${result.portalAccess.enabled ? "enabled" : "disabled"}`
      );
    } catch (error) {
      console.error("Error toggling portal access:", error);
      toast.error("Failed to update portal access");
    }
  };

  const handleSendLoginDetails = async () => {
    try {
      await dispatch(
        sendLoginDetails({
          clientId: client._id,
          email: client.email,
        })
      ).unwrap();

      toast.success("Login details sent successfully");
    } catch (error) {
      console.error("Error sending login details:", error);
      toast.error("Failed to send login details");
    }
  };

  const fetchAppointmentTypes = async () => {
    try {
      const response = await fetch("/api/appointment-types");
      const data = await response.json();
      if (response.ok) {
        setAppointmentTypes(data);
      }
    } catch (error) {
      logError("Failed to fetch appointment types", { error });
    }
  };

  const fetchClinicians = async () => {
    try {
      const response = await fetch("/api/clinicians");
      const data = await response.json();
      if (response.ok) {
        setClinicians(data);
      }
    } catch (error) {
      logError("Failed to fetch clinicians", { error });
    }
  };

  const toggleOnlineBooking = () => {
    const newSettings = {
      ...bookingSettings,
      canBookOnline: !bookingSettings.canBookOnline,
    };

    dispatch(
      updateBookingSettings({
        clientId: client._id,
        bookingSettings: newSettings,
      })
    )
      .unwrap()
      .then(() => {
        setBookingSettings(newSettings);
        toast.success("Online booking settings updated");
      })
      .catch((error) => {
        toast.error("Failed to update booking settings");
      });
  };

  const handleAppointmentTypeToggle = (typeId) => {
    const newAllowedTypes = bookingSettings.allowedAppointmentTypes.includes(
      typeId
    )
      ? bookingSettings.allowedAppointmentTypes.filter((id) => id !== typeId)
      : [...bookingSettings.allowedAppointmentTypes, typeId];

    const newSettings = {
      ...bookingSettings,
      allowedAppointmentTypes: newAllowedTypes,
    };

    dispatch(
      updateBookingSettings({
        clientId: client._id,
        bookingSettings: newSettings,
      })
    )
      .unwrap()
      .then(() => {
        setBookingSettings(newSettings);
        toast.success("Appointment types updated");
      })
      .catch(() => toast.error("Failed to update appointment types"));
  };

  const handleClinicianToggle = (clinicianId) => {
    const newAllowedClinicians = bookingSettings.allowedClinicians.includes(
      clinicianId
    )
      ? bookingSettings.allowedClinicians.filter((id) => id !== clinicianId)
      : [...bookingSettings.allowedClinicians, clinicianId];

    const newSettings = {
      ...bookingSettings,
      allowedClinicians: newAllowedClinicians,
    };

    dispatch(
      updateBookingSettings({
        clientId: client._id,
        bookingSettings: newSettings,
      })
    )
      .unwrap()
      .then(() => {
        setBookingSettings(newSettings);
        toast.success("Clinician access updated");
      })
      .catch(() => toast.error("Failed to update clinician access"));
  };

  const toggleParentAccess = async (parentIndex, emailIndex) => {
    try {
      const updatedParentAccess = [...parentAccess];
      updatedParentAccess[parentIndex].emails[emailIndex].hasAccess =
        !updatedParentAccess[parentIndex].emails[emailIndex].hasAccess;

      const response = await fetch(`/api/clients/${client._id}/parent-access`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parentAccess: updatedParentAccess }),
      });

      const data = await response.json();
      if (response.ok) {
        setParentAccess(updatedParentAccess);
        dispatch(
          createPortalLog(client._id, {
            action: "PARENT_ACCESS_UPDATED",
            details: `Parent access updated for ${updatedParentAccess[parentIndex].name}`,
          })
        );
        toast.success("Parent access updated");
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      logError("Failed to update parent access", { error });
      toast.error("Failed to update parent access");
    }
  };

  const handlePasswordReset = async () => {
    try {
      const response = await fetch(
        `/api/clients/${client._id}/reset-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );
      const data = await response.json();
      if (response.ok) {
        dispatch(
          createPortalLog(client._id, {
            action: "PASSWORD_RESET_REQUESTED",
            details: "Password reset email sent to client",
          })
        );
        toast.success("Password reset email sent");
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      logError("Failed to send password reset", { error });
      toast.error("Failed to send password reset email");
    }
  };

  const handleServiceSelection = (selectedService) => {
    // Check if the service is already in the allowed types
    const isAlreadyAllowed = bookingSettings.allowedAppointmentTypes.some(
      (type) => type._id === selectedService._id
    );

    if (!isAlreadyAllowed) {
      // Add the service to the allowed types
      const newAllowedTypes = [
        ...bookingSettings.allowedAppointmentTypes,
        selectedService,
      ];

      const newSettings = {
        ...bookingSettings,
        allowedAppointmentTypes: newAllowedTypes,
      };

      dispatch(
        updateBookingSettings({
          clientId: client._id,
          bookingSettings: newSettings,
        })
      )
        .unwrap()
        .then(() => {
          setBookingSettings(newSettings);
          toast.success("Bookable service added");
        })
        .catch(() => toast.error("Failed to add bookable service"));
    }
  };

  const handleClinicianSelection = (selectedClinician) => {
    // Check if the clinician is already in the allowed clinicians
    const isAlreadyAllowed = bookingSettings.allowedClinicians.some(
      (clinician) => clinician._id === selectedClinician._id
    );

    if (!isAlreadyAllowed) {
      // Add the clinician to the allowed clinicians
      const newAllowedClinicians = [
        ...bookingSettings.allowedClinicians,
        selectedClinician,
      ];

      const newSettings = {
        ...bookingSettings,
        allowedClinicians: newAllowedClinicians,
      };

      dispatch(
        updateBookingSettings({
          clientId: client._id,
          bookingSettings: newSettings,
        })
      )
        .unwrap()
        .then(() => {
          setBookingSettings(newSettings);
          toast.success("Bookable clinician added");
        })
        .catch(() => toast.error("Failed to add bookable clinician"));
    }
  };

  const handleClinicSelection = (selectedClinic) => {
    // Check if the clinic is already in the allowed clinics
    const isAlreadyAllowed = bookingSettings.allowedClinics.some(
      (clinic) => clinic._id === selectedClinic._id
    );

    if (!isAlreadyAllowed) {
      // Add the clinic to the allowed clinics
      const newAllowedClinics = [
        ...bookingSettings.allowedClinics,
        selectedClinic,
      ];

      const newSettings = {
        ...bookingSettings,
        allowedClinics: newAllowedClinics,
      };

      dispatch(
        updateBookingSettings({
          clientId: client._id,
          bookingSettings: newSettings,
        })
      )
        .unwrap()
        .then(() => {
          setBookingSettings(newSettings);
          toast.success("Bookable clinic added");
        })
        .catch(() => toast.error("Failed to add bookable clinic"));
    }
  };

  const removeSelectedService = (serviceId) => {
    // Remove the service from the allowed types
    const newAllowedTypes = bookingSettings.allowedAppointmentTypes.filter(
      (type) => type._id !== serviceId
    );

    const newSettings = {
      ...bookingSettings,
      allowedAppointmentTypes: newAllowedTypes,
    };

    dispatch(
      updateBookingSettings({
        clientId: client._id,
        bookingSettings: newSettings,
      })
    )
      .unwrap()
      .then(() => {
        setBookingSettings(newSettings);
        toast.success("Bookable service removed");
      })
      .catch(() => toast.error("Failed to remove bookable service"));
  };

  const removeSelectedClinician = (clinicianId) => {
    // Remove the clinician from the allowed clinicians
    const newAllowedClinicians = bookingSettings.allowedClinicians.filter(
      (clinician) => clinician._id !== clinicianId
    );

    const newSettings = {
      ...bookingSettings,
      allowedClinicians: newAllowedClinicians,
    };

    dispatch(
      updateBookingSettings({
        clientId: client._id,
        bookingSettings: newSettings,
      })
    )
      .unwrap()
      .then(() => {
        setBookingSettings(newSettings);
        toast.success("Bookable clinician removed");
      })
      .catch(() => toast.error("Failed to remove bookable clinician"));
  };

  const removeSelectedClinic = (clinicId) => {
    // Remove the clinic from the allowed clinics
    const newAllowedClinics = bookingSettings.allowedClinics.filter(
      (clinic) => clinic._id !== clinicId
    );

    const newSettings = {
      ...bookingSettings,
      allowedClinics: newAllowedClinics,
    };

    dispatch(
      updateBookingSettings({
        clientId: client._id,
        bookingSettings: newSettings,
      })
    )
      .unwrap()
      .then(() => {
        setBookingSettings(newSettings);
        toast.success("Bookable clinic removed");
      })
      .catch(() => toast.error("Failed to remove bookable clinic"));
  };

  const toggleServiceBooking = (serviceId) => {
    setSelectedServices((services) =>
      services.map((service) =>
        service._id === serviceId
          ? { ...service, enabled: !service.enabled }
          : service
      )
    );

    const updatedSettings = {
      ...bookingSettings,
      allowedAppointmentTypes: selectedServices
        .filter((service) => service.enabled)
        .map((service) => service._id),
    };
    updateBookingSettings(updatedSettings);
  };

  const removeService = (serviceId) => {
    setSelectedServices((services) =>
      services.filter((service) => service._id !== serviceId)
    );

    const updatedSettings = {
      ...bookingSettings,
      allowedAppointmentTypes: selectedServices
        .filter((service) => service._id !== serviceId && service.enabled)
        .map((service) => service._id),
    };
    updateBookingSettings(updatedSettings);
  };

  const columns = [
    {
      header: "Date",
      accessor: "timestamp",
      cell: ({ value }) => new Date(value).toLocaleString(),
    },
    {
      header: "Action",
      accessor: "action",
    },
    {
      header: "Details",
      accessor: "details",
    },
  ];

  return (
    <div className="bg-white rounded-sm shadow-sm border border-gray-200 p-6 space-y-6">
      <ToastContainer />
      
      {/* Portal Access Management Section */}
      <div className="border border-gray-200 rounded-sm bg-gray-50">
        <div
          className="flex items-center justify-between cursor-pointer p-4 hover:bg-gray-100 transition-colors"
          onClick={() => setIsAccordionOpen(!isAccordionOpen)}>
          <div className="flex items-center space-x-4">
            <div className="h-10 w-10 rounded-full bg-gray-900 flex items-center justify-center">
              <KeyIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Portal Access Management
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                Control client portal access and permissions
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                client.portalAccess?.enabled
                  ? "bg-green-100 text-green-800 border border-green-200"
                  : "bg-gray-100 text-gray-800 border border-gray-200"
              }`}>
              {client.portalAccess?.enabled ? "Active" : "Inactive"}
            </span>
            {isAccordionOpen ? (
              <ChevronUpIcon className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-gray-500" />
            )}
          </div>
        </div>

        {isAccordionOpen && (
          <div className="border-t border-gray-200 bg-white">
            <div className="p-4">
              <div className="overflow-hidden rounded-sm border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Email
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Portal Access
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {emailAccess.map((email, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {email.firstName} {email.lastName}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {email.email}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <Switch
                            checked={email.hasAccess}
                            onChange={() => handlePortalAccessToggle(email.email)}
                            className={`${
                              email.hasAccess ? "bg-gray-900" : "bg-gray-200"
                            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2`}>
                            <span
                              className={`${
                                email.hasAccess
                                  ? "translate-x-6"
                                  : "translate-x-1"
                              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                            />
                          </Switch>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          {email.hasAccess && (
                            <button
                              onClick={handleSendLoginDetails}
                              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 shadow-sm transition-colors">
                              <EnvelopeIcon className="h-4 w-4 mr-1.5" />
                              Send Login Details
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {client?.portalAccess?.enabled && (
        <>
          {/* Online Booking Settings Section */}
          <div className="border border-gray-200 rounded-sm bg-gray-50">
            <div
              className="flex items-center justify-between cursor-pointer p-4 hover:bg-gray-100 transition-colors"
              onClick={() =>
                setIsBookingAccordionOpen(!isBookingAccordionOpen)
              }>
              <div className="flex items-center space-x-4">
                <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center">
                  <CalendarIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Online Booking Settings
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Configure appointment booking preferences
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    bookingSettings.canBookOnline
                      ? "bg-green-100 text-green-800 border border-green-200"
                      : "bg-gray-100 text-gray-800 border border-gray-200"
                  }`}>
                  {bookingSettings.canBookOnline ? "Enabled" : "Disabled"}
                </span>
                {isBookingAccordionOpen ? (
                  <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                )}
              </div>
            </div>

            {isBookingAccordionOpen && (
              <div className="border-t border-gray-200 bg-white p-4">
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-sm border border-gray-200">
                    <div>
                      <span className="text-sm font-medium text-gray-900">
                        Enable Online Booking
                      </span>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Allow this client to book appointments online
                      </p>
                    </div>
                    <Switch
                      checked={bookingSettings.canBookOnline}
                      onChange={toggleOnlineBooking}
                      className={`${
                        bookingSettings.canBookOnline
                          ? "bg-gray-900"
                          : "bg-gray-200"
                      } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2`}>
                      <span
                        className={`${
                          bookingSettings.canBookOnline
                            ? "translate-x-6"
                            : "translate-x-1"
                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                      />
                    </Switch>
                  </div>

                  {bookingSettings.canBookOnline && (
                    <div className="space-y-6">
                      {/* Bookable Services */}
                      <div className="border border-gray-200 rounded-sm p-4">
                        <div className="mb-3">
                          <h4 className="text-sm font-semibold text-gray-900 mb-1">
                            Bookable Services
                          </h4>
                          <p className="text-xs text-gray-500">
                            Select which services this client can book online
                          </p>
                        </div>
                        <SearchDropdown
                          prefix=":service:"
                          displayFields={[
                            "serviceCode",
                            "description",
                            "price",
                          ]}
                          onSelection={handleServiceSelection}
                          placeholder="Search for a service..."
                          activeServices={true}
                        />

                        {bookingSettings.allowedAppointmentTypes.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {bookingSettings.allowedAppointmentTypes.map(
                              (service) => (
                                <div
                                  key={service._id}
                                  className="flex items-center bg-blue-50 text-blue-800 px-3 py-1.5 rounded-full border border-blue-200 shadow-sm">
                                  <span className="text-sm font-medium">
                                    {service.description || service.serviceCode}
                                  </span>
                                  <button
                                    onClick={() =>
                                      removeSelectedService(service._id)
                                    }
                                    className="ml-2 hover:text-red-600 transition-colors">
                                    <XMarkIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              )
                            )}
                          </div>
                        )}
                      </div>

                      {/* Bookable Clinicians */}
                      <div className="border border-gray-200 rounded-sm p-4">
                        <div className="mb-3">
                          <h4 className="text-sm font-semibold text-gray-900 mb-1">
                            Bookable Clinicians
                          </h4>
                          <p className="text-xs text-gray-500">
                            Select which clinicians this client can book with
                          </p>
                        </div>
                        <SearchDropdown
                          prefix=":clinician:"
                          displayFields={[
                            "firstName",
                            "lastName",
                            "qualifications",
                          ]}
                          onSelection={handleClinicianSelection}
                          placeholder="Search for a clinician"
                        />

                        {bookingSettings.allowedClinicians.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {bookingSettings.allowedClinicians.map(
                              (clinician) => (
                                <div
                                  key={clinician._id}
                                  className="flex items-center bg-green-50 text-green-800 px-3 py-1.5 rounded-full border border-green-200 shadow-sm">
                                  <span className="text-sm font-medium">
                                    {clinician.firstName} {clinician.lastName}
                                  </span>
                                  <button
                                    onClick={() =>
                                      removeSelectedClinician(clinician._id)
                                    }
                                    className="ml-2 hover:text-red-600 transition-colors">
                                    <XMarkIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              )
                            )}
                          </div>
                        )}
                      </div>

                      {/* Bookable Clinics */}
                      <div className="border border-gray-200 rounded-sm p-4">
                        <div className="mb-3">
                          <h4 className="text-sm font-semibold text-gray-900 mb-1">
                            Bookable Clinics
                          </h4>
                          <p className="text-xs text-gray-500">
                            Select which clinic locations this client can book at
                          </p>
                        </div>
                        <SearchDropdown
                          prefix=":clinic:"
                          displayFields={[
                            "name",
                            "phoneNumber",
                            "address.street",
                            "address.city",
                            "address.state",
                            "address.zipCode",
                            "address.country",
                          ]}
                          onSelection={handleClinicSelection}
                          placeholder="Search for a clinic"
                        />

                        {bookingSettings.allowedClinics.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {bookingSettings.allowedClinics.map((clinic) => (
                              <div
                                key={clinic._id}
                                className="flex items-center bg-purple-50 text-purple-800 px-3 py-1.5 rounded-full border border-purple-200 shadow-sm">
                                <span className="text-sm font-medium">{clinic.name}</span>
                                <button
                                  onClick={() => removeSelectedClinic(clinic._id)}
                                  className="ml-2 hover:text-red-600 transition-colors">
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Parent Portal Access Section */}
          {client?.isChild && (
            <div className="border border-gray-200 rounded-sm">
              <div className="p-4 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center space-x-4">
                  <div className="h-10 w-10 rounded-full bg-orange-600 flex items-center justify-center">
                    <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Parent Portal Access
                    </h3>
                    <p className="text-sm text-gray-500 mt-0.5">
                      Manage parent access to this child's portal
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-white">
                <div className="space-y-4">
                  {parentAccess.map((parent, parentIndex) => (
                    <div
                      key={parentIndex}
                      className="border border-gray-200 rounded-sm p-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">{parent.name}</h4>
                      <div className="space-y-3">
                        {parent.emails.map((email, emailIndex) => (
                          <div
                            key={emailIndex}
                            className="flex items-center justify-between p-2 bg-gray-50 rounded-sm">
                            <span className="text-sm text-gray-700 font-medium">
                              {email.address}
                            </span>
                            <Switch
                              checked={email.hasAccess}
                              onChange={() =>
                                toggleParentAccess(parentIndex, emailIndex)
                              }
                              className={`${
                                email.hasAccess ? "bg-gray-900" : "bg-gray-200"
                              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2`}>
                              <span
                                className={`${
                                  email.hasAccess
                                    ? "translate-x-6"
                                    : "translate-x-1"
                                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                              />
                            </Switch>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Portal Activity Logs Section */}
          <div className="border border-gray-200 rounded-sm">
            <div className="p-4 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center space-x-4">
                <div className="h-10 w-10 rounded-full bg-gray-700 flex items-center justify-center">
                  <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Portal Activity Logs
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Track client portal usage and activities
                  </p>
                </div>
              </div>
            </div>
            <div className="p-4 bg-white">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-pulse flex space-x-4">
                    <div className="rounded-sm bg-gray-200 h-12 w-12"></div>
                    <div className="flex-1 space-y-2 py-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                </div>
              ) : error ? (
                <div className="text-center py-8">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-sm bg-red-100">
                    <svg className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading logs</h3>
                  <p className="mt-1 text-sm text-gray-500">{error}</p>
                </div>
              ) : logs.length > 0 ? (
                <div className="overflow-hidden rounded-sm border border-gray-200">
                  <Table data={logs} columns={columns} className="w-full" />
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-sm bg-gray-100">
                    <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No activity yet</h3>
                  <p className="mt-1 text-sm text-gray-500">Portal activity will appear here once the client starts using their portal.</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
